/*
 * AddressFinder adapter for the member address dialog
 * (assets/twc-personal-details.js) and the gift card join form
 * (assets/twc-redeem.js).
 *
 * The public browser licence key is supplied by Liquid at runtime. It does
 * not live in this asset. The widget script is fetched only when an address
 * form opens; a missing key or failed download leaves the manual address
 * fields unchanged and usable.
 */
(function (global) {
  'use strict';

  if (global.TwcAddressFinder) return;

  var SCRIPT_URL = 'https://api.addressfinder.io/assets/v3/widget.js';
  var loadPromise = null;

  var NZ_PROVINCES = {
    auckland: 'AUK',
    'bay of plenty': 'BOP',
    canterbury: 'CAN',
    'chatham islands': 'CIT',
    gisborne: 'GIS',
    'hawkes bay': 'HKB',
    'manawatu wanganui': 'MWT',
    'manawatu whanganui': 'MWT',
    marlborough: 'MBH',
    nelson: 'NSN',
    northland: 'NTL',
    otago: 'OTA',
    southland: 'STL',
    taranaki: 'TKI',
    tasman: 'TAS',
    waikato: 'WKO',
    wellington: 'WGN',
    'west coast': 'WTC',
  };

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function normaliseName(value) {
    var out = clean(value).toLowerCase();
    if (out.normalize) out = out.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return out
      .replace(/&/g, ' and ')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\bregion\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function provinceCode(country, value) {
    var raw = clean(value);
    if (!raw) return '';
    if (country === 'AU') return raw.toUpperCase();
    if (/^[A-Z]{3}$/.test(raw.toUpperCase())) return raw.toUpperCase();
    return NZ_PROVINCES[normaliseName(raw)] || '';
  }

  function joinParts(parts) {
    return parts.map(clean).filter(Boolean).join(', ');
  }

  function mapMetadata(country, metadata) {
    var meta = metadata || {};
    if (country === 'NZ') {
      return {
        // The NZ postal shape has both a suburb and a city. Combine the postal
        // street lines so neither value is lost from Shopify's two-line form.
        address1: joinParts([meta.address_line_1, meta.address_line_2]) || clean(meta.street),
        address2: clean(meta.suburb),
        city: clean(meta.city || meta.mailtown || meta.suburb),
        province: provinceCode('NZ', meta.region),
        zip: clean(meta.postcode),
      };
    }

    return {
      address1: clean(meta.address_line_1 || meta.address_line_combined),
      address2: clean(meta.address_line_2),
      city: clean(meta.locality_name),
      province: provinceCode('AU', meta.state_territory),
      zip: clean(meta.postcode),
    };
  }

  function widgetOptions(country, container) {
    var options = {
      container: container,
      max_results: 5,
      // A custom container makes AddressFinder leave top/left positioning to
      // the host. The address field is that container, so absolute positioning
      // keeps the list attached to the input while the modal body scrolls.
      position: 'absolute',
    };
    // Use both the physical-address and Australia Post datasets. Omitting
    // the PO Box and rural filters keeps those valid address types available.
    if (country === 'AU') {
      options.address_params = { source: 'gnaf,paf' };
      options.address_metadata_params = { source: 'gnaf,paf' };
    }
    return options;
  }

  function loadWidget() {
    if (global.AddressFinder && global.AddressFinder.Widget) {
      return Promise.resolve(global.AddressFinder);
    }
    if (loadPromise) return loadPromise;

    loadPromise = new Promise(function (resolve, reject) {
      var script = global.document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = function () {
        if (global.AddressFinder && global.AddressFinder.Widget) resolve(global.AddressFinder);
        else reject(new Error('AddressFinder did not initialise'));
      };
      script.onerror = function () { reject(new Error('AddressFinder did not load')); };
      global.document.body.appendChild(script);
    }).catch(function () {
      loadPromise = null;
      return null;
    });

    return loadPromise;
  }

  function attach(config) {
    if (!config || !config.input || !clean(config.key)) return Promise.resolve(null);
    var country = clean(config.country).toUpperCase() === 'NZ' ? 'NZ' : 'AU';

    return loadWidget().then(function (library) {
      if (!library) return null;
      var widget = new library.Widget(
        config.input,
        clean(config.key),
        country,
        widgetOptions(country, config.container || global.document.body)
      );
      widget.on('address:select', function (_fullAddress, metadata) {
        if (typeof config.onSelect === 'function') {
          config.onSelect(mapMetadata(country, metadata));
        }
      });
      return widget;
    }).catch(function () { return null; });
  }

  function detach(widget) {
    if (widget && typeof widget.disable === 'function') widget.disable();
  }

  global.TwcAddressFinder = {
    attach: attach,
    detach: detach,
    mapMetadata: mapMetadata,
    provinceCode: provinceCode,
    widgetOptions: widgetOptions,
  };
})(window);
